// apps/app/src/features/auth/accept-invite.page.tsx
//
// Where a team invitation lands. Before this page existed the emailed link
// opened the sign-in screen, and an invited colleague had no way to join.
//
// The invitation is looked up first, so the page can say which clinic and which
// address it is for — someone invited by a clinic they half-remember needs to
// know what they are signing up to before choosing a password.

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Building2, LockKeyhole, Mail, MailX, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, http } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { ROLE_INFO, type ClinicRole } from '@/lib/permissions';
import { AuthHeading, AuthLayout, PasswordInput } from './auth-layout';

interface Invite {
  email: string;
  role: ClinicRole;
  clinicName: string;
}

export function AcceptInvitePage() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const { data: invite, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => http.get<Invite>(`/users/invite?token=${encodeURIComponent(token)}`),
    enabled: token.length >= 10,
    retry: false,
  });

  if (!token || error) {
    return (
      <AuthLayout>
        <AuthHeading icon={MailX} title={t('invite.expiredTitle')} subtitle={t('invite.expiredBody')} />
        <Button asChild className="h-11 w-full">
          <Link to="/login">{t('invite.goToSignIn')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  if (isLoading || !invite) {
    return (
      <AuthLayout>
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </AuthLayout>
    );
  }

  return <JoinForm token={token} invite={invite} />;
}

function JoinForm({ token, invite }: { token: string; invite: Invite }) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const login = useAuth((s) => s.login);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await http.post('/users/accept-invite', { token, password, firstName, lastName });
      // Straight in, rather than to a sign-in form asking for what they just chose.
      await login(invite.email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('invite.error'));
      setBusy(false);
    }
  };

  const info = ROLE_INFO[invite.role];
  const role = info ? t(info.labelKey) : t('invite.teamMember');

  return (
    <AuthLayout>
      <AuthHeading
        icon={Users}
        title={t('invite.title', { clinic: invite.clinicName })}
        subtitle={
          <Trans t={t} i18nKey="invite.intro" values={{ role }} components={{ strong: <span className="font-medium text-foreground" /> }} />
        }
      />
      <form onSubmit={submit} className="space-y-5">

        <div className="space-y-1.5 rounded-lg border bg-muted/40 p-3 text-sm">
          <p className="flex items-center gap-2">
            <Building2 className="size-4 text-muted-foreground" aria-hidden="true" />
            {invite.clinicName}
          </p>
          <p className="flex items-center gap-2">
            <Mail className="size-4 text-muted-foreground" aria-hidden="true" />
            {invite.email}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="join-first">{t('fields.firstName')}</Label>
            <Input id="join-first" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="join-last">{t('fields.lastName')}</Label>
            <Input id="join-last" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-11" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="join-password">{t('fields.choosePassword')}</Label>
          <PasswordInput
            id="join-password"
            icon={LockKeyhole}
            required
            minLength={12}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="block text-xs text-muted-foreground">{t('password.minLengthHint')}</span>
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <Button loading={busy} disabled={password.length < 12} className="h-11 w-full">
          {t('invite.submit', { clinic: invite.clinicName })}
        </Button>
      </form>
    </AuthLayout>
  );
}
